import { useRef, type ReactNode } from "react";
import { useKpis } from "../lib/use-kpis.js";
import {
  avisoDeFonteKpi,
  classeDeFonteKpi,
  classificarFonteKpi,
  dicaDeFonteKpi,
  erroSemDadoKpi,
} from "../lib/kpi-fonte.js";
import { useLiveEvent } from "../lib/useLiveEvents.js";
import { LiveIndicator } from "../components/LiveIndicator.js";
import { Skeleton } from "../components/Skeleton.js";
import { DashboardOperationsPanel, useDashboardOperations } from "../components/DashboardOperationsPanel.js";
import {
  IconBolt,
  IconBell,
  IconChargers,
  IconTrend,
} from "../components/icons.js";
import "../styles/dashboard-operations.css";
import "../styles/dashboard-stale.css";

/*
 * Dashboard — cabeçalho unificado (título + eyebrow + status ao vivo do
 * mesmo lado) e KPIs com ícone em círculo suave em vez de border-left
 * colorido em 4 cores diferentes. Cor no card antigo era só decoração:
 * o operador não lia "azul = energia"; lia "outro azul de novo". Agora
 * a cor só aparece em estado semântico (uptime% verde, energia cyan).
 */

function KpiCard({
  label,
  value,
  hint,
  accent,
  icon,
  classeExtra = "",
}: {
  label: string;
  value: string;
  hint?: string;
  accent?: "orange" | "cyan" | "navy" | "success" | "danger";
  icon: ReactNode;
  /** Degradação STALE — ver `kpi-fonte.ts` e `dashboard-stale.css`. */
  classeExtra?: string;
}) {
  return (
    <div className={`nx-kpi nx-kpi--${accent ?? "navy"}${classeExtra}`}>
      <div className="nx-kpi__icon">{icon}</div>
      <div className="nx-kpi__label">{label}</div>
      <div className="nx-kpi__value">{value}</div>
      {hint && <div className="nx-kpi__hint">{hint}</div>}
    </div>
  );
}

function fmtDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtEnergy(wh: number | null): string {
  if (wh == null) return "—";
  if (wh >= 1000) return `${(wh / 1000).toFixed(2)} kWh`;
  return `${wh} Wh`;
}

function fmtHora(ms: number): string {
  return new Date(ms).toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Timestamp curto para o subtítulo — só HH:mm porque a data ela mesma já
// está implícita ("agora"). Manter localizado em pt-BR.
function fmtNow(): string {
  return new Date().toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function DashboardPage() {
  const { kpis, error, refresh } = useKpis();
  const operations = useDashboardOperations();

  // Quando chegou a última leitura BOA. O `useKpis` não expõe isso e não
  // pode ser alterado nesta entrega, mas dá para derivar aqui: `setKpis`
  // recebe o objeto novo de `res.json()` a cada resposta ok, então uma
  // identidade diferente é, por construção, uma leitura nova. Guardado em
  // `ref` e não em `state` porque o polling é de 1s: um `setState` por
  // segundo só para carimbar hora custaria um render a mais por segundo
  // numa tela com tabela.
  const ultimaLeituraBoa = useRef<number | null>(null);
  const kpisCarimbados = useRef<typeof kpis>(null);
  if (kpis && kpis !== kpisCarimbados.current) {
    kpisCarimbados.current = kpis;
    ultimaLeituraBoa.current = Date.now();
  }

  // Push ao vivo: sempre que uma transação nasce ou fecha, agenda um
  // refresh no próximo tick pra recalcular KPIs sem full refetch
  // imediato. O polling do próprio useKpis segue como fallback (10s).
  useLiveEvent("transaction.started", () => { void refresh(); });
  useLiveEvent("transaction.ended", () => { void refresh(); });

  // Sem NENHUM dado não há o que degradar: a tela é o erro. O 401 ganha
  // texto próprio porque "Erro ao carregar KPIs: 401" não diz a ninguém
  // que a sessão acabou.
  if (error && !kpis) {
    return (
      <div className="nx-card">
        <div className="nx-error">{erroSemDadoKpi(error)}</div>
      </div>
    );
  }

  // Skeleton state: mesma estrutura da tela pronta (4 KPIs + tabela) para
  // não haver "layout shift" quando os dados chegam. O <Skeleton kpi>
  // ocupa exatamente 96px de altura do card, e as linhas da tabela usam
  // <Skeleton row> na altura padrão de 40px. Sem essa reserva, o operador
  // clica onde a mensagem "Carregando…" está e acerta um item que só
  // apareceu 400ms depois.
  if (!kpis) {
    return (
      <div>
        <div className="nx-page-head">
          <div className="nx-page-head__meta">
            <span className="nx-page-head__eyebrow">Visão geral</span>
            <h1>Dashboard</h1>
            <p className="nx-page-head__sub">
              Carregando indicadores…
            </p>
          </div>
          <div className="nx-page-head__actions">
            <LiveIndicator />
          </div>
        </div>

        <div className="nx-kpi-grid" aria-busy="true" aria-label="Carregando indicadores">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={`kpi-skel-${i}`} variant="kpi" />
          ))}
        </div>

        <div className="nx-card" style={{ marginTop: "var(--nx-space-6)" }}>
          <h2
            style={{
              fontSize: "var(--nx-text-md)",
              marginBottom: "var(--nx-space-3)",
            }}
          >
            Últimas transações
          </h2>
          <Skeleton variant="row" count={5} ariaLabel="Carregando últimas transações" />
        </div>
      </div>
    );
  }

  const charging = operations.data?.conectores.charging ?? kpis.activeTransactions;
  const faults = operations.data?.conectores.faulted;

  // Daqui para baixo, HÁ dado na tela. A pergunta deixa de ser "consegui
  // ler?" e passa a ser "o que está aqui ainda vale?".
  const fonte = classificarFonteKpi(error);
  const horaUltima =
    ultimaLeituraBoa.current === null ? null : fmtHora(ultimaLeituraBoa.current);
  const aviso = avisoDeFonteKpi(fonte, horaUltima);
  const classeKpi = classeDeFonteKpi(fonte);
  // Com a fonte parada, toda dica de card vira "de quando é este número" —
  // "estado OCPP atual" ao lado de um valor herdado é a própria mentira.
  const dica = (viva: string) => (fonte === "viva" ? viva : dicaDeFonteKpi(horaUltima));

  return (
    <div>
      <div className="nx-page-head">
        <div className="nx-page-head__meta">
          <span className="nx-page-head__eyebrow">Visão geral</span>
          <h1>Dashboard</h1>
          <p className="nx-page-head__sub">
            {fonte === "viva"
              ? `Estado da operação · atualizado ${fmtNow()}`
              : horaUltima
                ? `Estado da operação · sem atualizar desde ${horaUltima}`
                : "Estado da operação · sem atualizar"}
          </p>
        </div>
        <div className="nx-page-head__actions">
          <LiveIndicator />
        </div>
      </div>

      {/* `role="status"` e não `alert`: a tela continua utilizável e o
          conteúdo continua ali — o que mudou foi a validade dele. */}
      {aviso && (
        <div className="nx-kpi-aviso" role="status">
          <span className="nx-kpi-aviso__ponto" aria-hidden="true" />
          <span>{aviso}</span>
        </div>
      )}

      <div className="nx-kpi-grid">
        <KpiCard
          label="Total de carregadores"
          value={String(kpis.chargersTotal)}
          hint={dica(`${kpis.chargersOnline} online agora`)}
          accent="navy"
          icon={<IconChargers />}
          classeExtra={classeKpi}
        />
        <KpiCard
          label="Conectores em recarga"
          value={String(charging)}
          hint={dica(operations.data ? "estado OCPP atual" : "atualizando estado OCPP")}
          accent="orange"
          icon={<IconBolt />}
          classeExtra={classeKpi}
        />
        <KpiCard
          label="Energia entregue (24h)"
          value={fmtEnergy(kpis.energyLast24hWh)}
          hint={dica("sessões OCPP encerradas")}
          accent="cyan"
          icon={<IconTrend />}
          classeExtra={classeKpi}
        />
        <KpiCard
          label="Disponibilidade"
          value={`${kpis.availabilityPct.toFixed(1)}%`}
          hint={dica(
            faults === undefined ? "atualizando falhas" : `${faults} conector(es) requerem atenção`,
          )}
          accent={faults && faults > 0 ? "danger" : "success"}
          icon={<IconBell />}
          classeExtra={classeKpi}
        />
      </div>

      <DashboardOperationsPanel data={operations.data} error={operations.error} />

      <div className="nx-card" style={{ marginTop: "var(--nx-space-6)" }}>
        <h2
          style={{
            fontSize: "var(--nx-text-md)",
            marginBottom: "var(--nx-space-3)",
          }}
        >
          Últimas transações
          {fonte !== "viva" && (
            // Mesma origem dos KPIs, mesma validade: a lista também é o
            // último snapshot, e um "em curso" aqui pode ter terminado.
            <span
              style={{
                marginLeft: "var(--nx-space-2)",
                color: "var(--nx-mute)",
                fontSize: "var(--nx-text-xs)",
                fontWeight: 400,
              }}
            >
              · {dicaDeFonteKpi(horaUltima)}
            </span>
          )}
        </h2>
        {kpis.recentTransactions.length === 0 ? (
          <p style={{ color: "var(--nx-mute)", margin: 0 }}>
            Sem transações registradas ainda.
          </p>
        ) : (
          <div className="nx-table-wrap">
            <table className="nx-table">
              <thead>
                <tr>
                  <th className="id">#</th>
                  <th>Carregador</th>
                  <th className="id">Conector</th>
                  <th>Cartão</th>
                  <th>Início</th>
                  <th>Fim</th>
                  <th className="num">Energia</th>
                  <th>Motivo</th>
                </tr>
              </thead>
              <tbody>
                {kpis.recentTransactions.map((t) => (
                  <tr key={t.ocppTransactionId}>
                    <td className="id mono">#{t.ocppTransactionId}</td>
                    <td className="mono">{t.cpId}</td>
                    <td className="id mono">{t.connectorId}</td>
                    <td className="mono">{t.idTag}</td>
                    <td>{fmtDate(t.startTime)}</td>
                    <td>
                      {t.stopTime ? (
                        fmtDate(t.stopTime)
                      ) : (
                        <span className="nx-badge nx-badge--charging">
                          em curso
                        </span>
                      )}
                    </td>
                    <td className="num mono">{fmtEnergy(t.energyWh)}</td>
                    <td>{t.stopReason ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
